import React, { useState, useEffect } from "react";
import useApi from "../../hooks/useApi";
import DatePicker from "react-datepicker";
import moment from "moment";
import { activeStatus } from "../../widgets/PolicyItemStatus";
import Select from "react-select";
import useApp from "../../hooks/useApp";
import useReload from "../../hooks/useReload";
import TableWidget from "../../widgets/TableWidget";
import FileWidget from "../../widgets/FileWidget";
import { useQuery } from "react-query";
import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import { Form, InputGroup } from "react-bootstrap";

export const ClientPolicyRequirement = (props) => {
  const { itemid = null } = useParams();
  const { hideSave = false } = props;
  const [policy, setPolicy] = useState({});
  const [requirements, setRequirements] = useState([]);
  const [policyItemRequirement, setPolicyItemRequirement] = useState({});
  const { showAlert } = useApp();
  const { toggleReload, reload } = useReload();
  const navigate = useNavigate();

  const { api, abortSignal, auth } = useApi();

  const getPolicy = async () => {
    try {
      if (itemid) {
        const { data = {} } = await api.read(
          `/policies/policy-items/${itemid}`
        );
        setPolicy(data);
      }
    } catch (error) {}
  };

  useEffect(() => {
    getPolicy();
  }, [itemid, auth, reload]);

  const getRequirements = async () => {
    const { data: ptr } = await api.list(
      `/policies/policy-types/${policy?.PolicyTypeId}/requirements`
    );

    const { data: pr } = await api.list(
      `/products/${policy?.ProductId}/requirements`
    );

    let removeDups = [
      ...ptr.map((prod) => {
        return {
          ...prod,
          sequence: prod?.PolicyTypeRequirement?.sequence || 10,
        };
      }),
      ...pr.map((prod) => {
        return {
          ...prod,
          sequence: prod?.ProductRequirement?.sequence || 10,
        };
      }),
    ];
    removeDups = removeDups.filter(
      (value, index, self) =>
        index ===
        self.findIndex((t) => t.place === value.place && t.name === value.name)
    );

    //sort based on sequence
    removeDups.sort(function (a, b) {
      return a.sequence - b.sequence;
    });

    if (policy?.policyRequirement) {
      const selectTypeRequirements = removeDups.filter(
        (element) => element.type === "select"
      );

      const saveRequirements = JSON.parse(policy?.policyRequirement);

      selectTypeRequirements.forEach((requirement) => {
        for (const [key, value] of Object.entries(saveRequirements)) {
          if (key === requirement.id) {
            saveRequirements[key] = { label: value, value: value };
          }
        }
      });

      setPolicyItemRequirement(saveRequirements);
    }

    setRequirements([...removeDups]);
  };

  useEffect(() => {
    getRequirements();
  }, [policy]);

  const saveRequirement = async (modifiedRequirementData = null) => {
    const selectTypeRequirements = requirements.filter(
      (element) => element.type === "select"
    );

    const fileTypeRequirements = requirements.filter(
      (element) => element.type === "file"
    );

    try {
      let requirementData = modifiedRequirementData
        ? modifiedRequirementData
        : policyItemRequirement;

      selectTypeRequirements.forEach((requirement) => {
        let requirementValue = requirementData[requirement.id]?.value || "";

        requirementData = {
          ...requirementData,
          [requirement.id]: requirementValue,
        };
      });

      //upload the files
      const uploads = [];
      const filesUploaded = [];
      const filesRemoved = [];
      let filesToRemove = [];

      fileTypeRequirements.forEach((requirement) => {
        let files = requirementData[requirement.id];

        if (files) {
          let filesRemoving = [];
          let filesRemovingRemote = [];
          let formData = new FormData();
          for (const [key, value] of Object.entries(files)) {
            if (value.local) {
              formData.append("files", value);
            }

            if (value.delete) {
              filesRemovingRemote.push(
                api.delete(`/files/upload/${value.filename}`)
              );
              filesRemoving.push(value);
            }
          }

          if (filesRemoving.length > 0) {
            filesToRemove.push({ [requirement.id]: filesRemoving });
            filesRemoved.push({ [requirement.id]: filesRemovingRemote });
          }

          let fileCount = 0;
          for (const pair of formData.entries()) {
            fileCount += 1;
          }

          if (fileCount) {
            uploads.push(api.add(`/files/upload`, formData));
            filesUploaded.push(requirement.id);
          }
        }
      });

      if (filesRemoved.length > 0) {
        try {
          filesRemoved.forEach(async (removing) => {
            //const removed = await Promise.all(removing);
            // console.log(removed);
          });

          filesToRemove.forEach((files, index) => {
            for (const [key, value] of Object.entries(files)) {
              //console.log(requirementData[key]);
              requirementData = {
                ...requirementData,
                [key]: [
                  ...requirementData[key].filter((file) => !file.delete == 1),
                ],
              };

              setPolicyItemRequirement((requirement) => {
                return {
                  ...requirement,
                  [key]: [
                    ...requirement[key].filter((file) => !file.delete == 1),
                  ],
                };
              });
            }
          });
        } catch (error) {
          console.error(error);
        }
      }

      const uploaded = await Promise.all(uploads);

      if (uploaded) {
        uploaded.forEach((files, index) => {
          if (files.ok) {
            requirementData = {
              ...requirementData,
              [filesUploaded[index]]: [
                ...requirementData[filesUploaded[index]].filter(
                  (file) => !file.local
                ),
                ...files.data,
              ],
            };
          }
        });
      }

      const data = {
        policyRequirement: JSON.stringify(requirementData),
      };

      const response = await api.update(
        `/policies/policy-items/${itemid}/requirement`,
        data
      );
      if (response.ok) {
        showAlert("Policy requirement updated", "success");
        toggleReload();
        // navigate(0);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveRequirement = async (e) => {
    e.preventDefault();
    await saveRequirement();
  };

  const handleRemoveUploadedFile = async (filename, field) => {
    const response = await api.delete(`/files/upload/${filename}`);

    if (response.ok) {
      setTimeout(async () => {
        //remove deleted file from list then save
        const filesLeft = policyItemRequirement[field].filter(
          (file) => !(file.filename === filename)
        );

        setPolicyItemRequirement({
          ...policyItemRequirement,
          [field]: filesLeft,
        });

        await saveRequirement({
          ...policyItemRequirement,
          [field]: filesLeft,
        });
      }, 3000);
    }
  };

  return (
    <form autoComplete="off" onSubmit={handleSaveRequirement}>
      <fieldset
        disabled={
          props?.readOnly
            ? true
            : activeStatus.includes(policy?.status)
            ? true
            : false
        }
      >
        {requirements &&
          requirements.map((element) => {
            const options = element.options;
            let defaultOption;
            if (element.type === "select") {
              defaultOption = "";
              element.options.split(",").forEach((option) => {
                if (option.charAt(0) === "*") {
                  defaultOption = {
                    value: option.slice(1),
                    label: option.slice(1),
                  };
                }
              });
            }
            return (
              <div className="row mb-3" key={element.id}>
                {element.type === "label" ? (
                  <div>
                    <h4>{element?.name}</h4>
                    <span>{element?.options}</span>
                  </div>
                ) : (
                  <>
                    <label
                      htmlFor={element.id}
                      className="col-sm-3 col-form-label text-primary"
                    >
                      {element.name}
                    </label>
                    <div className="col-sm-9">
                      {element.type === "check" ? (
                        <div className="form-check form-switch">
                          <input
                            id={element.id}
                            className="form-check-input mt-2"
                            type="checkbox"
                            checked={
                              policyItemRequirement[element.id] == true
                                ? true
                                : false
                            }
                            onChange={(e) =>
                              setPolicyItemRequirement({
                                ...policyItemRequirement,
                                [element.id]: e.target.checked,
                              })
                            }
                          />
                        </div>
                      ) : element.type === "date" ? (
                        element.options ? (
                          element.options === "year" ? (
                            <DatePicker
                              id={element.id}
                              className="form-control"
                              selected={
                                policyItemRequirement?.[element.id] &&
                                new Date(policyItemRequirement?.[element.id])
                              }
                              showPopperArrow={false}
                              showYearPicker
                              dateFormat="yyyy"
                              onChange={(date) =>
                                setPolicyItemRequirement({
                                  ...policyItemRequirement,
                                  [element.id]: date,
                                })
                              }
                              isClearable
                            />
                          ) : element.options === "month" ? (
                            <DatePicker
                              id={element.id}
                              className="form-control"
                              selected={
                                policyItemRequirement?.[element.id] &&
                                new Date(policyItemRequirement?.[element.id])
                              }
                              showPopperArrow={false}
                              showMonthYearPicker
                              dateFormat="MM/yyyy"
                              onChange={(date) =>
                                setPolicyItemRequirement({
                                  ...policyItemRequirement,
                                  [element.id]: date,
                                })
                              }
                              isClearable
                            />
                          ) : element.options === "time" ? (
                            <DatePicker
                              id={element.id}
                              className="form-control"
                              selected={
                                policyItemRequirement?.[element.id] &&
                                new Date(policyItemRequirement?.[element.id])
                              }
                              showPopperArrow={false}
                              showTimeSelect
                              showTimeSelectOnly
                              timeIntervals={15}
                              timeCaption="Time"
                              dateFormat="h:mm aa"
                              onChange={(date) =>
                                setPolicyItemRequirement({
                                  ...policyItemRequirement,
                                  [element.id]: date,
                                })
                              }
                              isClearable
                            />
                          ) : (
                            <DatePicker
                              id={element.id}
                              className="form-control"
                              selected={
                                policyItemRequirement?.[element.id] &&
                                new Date(policyItemRequirement?.[element.id])
                              }
                              showPopperArrow={false}
                              dateFormat="dd/MM/yyyy"
                              onChange={(date) =>
                                setPolicyItemRequirement({
                                  ...policyItemRequirement,
                                  [element.id]: date,
                                })
                              }
                              isClearable
                            />
                          )
                        ) : (
                          <DatePicker
                            id={element.id}
                            className="form-control"
                            showPopperArrow={false}
                            selected={
                              policyItemRequirement?.[element.id] &&
                              new Date(policyItemRequirement?.[element.id])
                            }
                            dateFormat="dd/MM/yyyy"
                            onChange={(date) =>
                              setPolicyItemRequirement({
                                ...policyItemRequirement,
                                [element.id]: date,
                              })
                            }
                            isClearable
                          />
                        )
                      ) : element.type === "select" ? (
                        <Select
                          id={element.id}
                          options={
                            element?.options
                              ? element?.options.split(",").map((option) => {
                                  return option.charAt(0) === "*"
                                    ? {
                                        value: option.slice(1),
                                        label: option.slice(1),
                                      }
                                    : { value: option, label: option };
                                })
                              : []
                          }
                          onChange={(selectedOption) =>
                            setPolicyItemRequirement({
                              ...policyItemRequirement,
                              [element.id]: selectedOption,
                            })
                          }
                          value={
                            policyItemRequirement[element.id]
                              ? policyItemRequirement[element.id]
                              : defaultOption
                          }
                        />
                      ) : element.type === "number" ? (
                        <>
                          <InputGroup className="mb-3">
                            <Form.Control
                              aria-label="Number (to the nearest value)"
                              type="number"
                              min={0}
                              id={element.id}
                              onChange={(e) =>
                                setPolicyItemRequirement({
                                  ...policyItemRequirement,
                                  [element.id]: e.target.value,
                                })
                              }
                              value={
                                policyItemRequirement[element.id]
                                  ? policyItemRequirement[element.id]
                                  : ""
                              }
                            />
                            <InputGroup.Text className="fw-bold">
                              {Number.parseFloat(
                                policyItemRequirement[element.id]
                              ).toLocaleString("en-US")}
                            </InputGroup.Text>
                          </InputGroup>
                        </>
                      ) : element.type === "table" ? (
                        <TableWidget
                          readOnly={props?.readOnly || false}
                          id={element.id}
                          data={element}
                          onChange={(value) =>
                            setPolicyItemRequirement({
                              ...policyItemRequirement,
                              [element.id]: JSON.stringify(value),
                            })
                          }
                          value={
                            policyItemRequirement[element.id]
                              ? JSON.parse(policyItemRequirement[element.id])
                              : ""
                          }
                        />
                      ) : element.type === "file" ? (
                        <FileWidget
                          id={element.id}
                          data={element}
                          onChange={(value) =>
                            setPolicyItemRequirement({
                              ...policyItemRequirement,
                              [element.id]: value,
                            })
                          }
                          // onUploadedFileRemove={handleRemoveUploadedFile}
                          value={
                            policyItemRequirement[element.id]
                              ? policyItemRequirement[element.id]
                              : ""
                          }
                        />
                      ) : element.type === "multilinetext" ? (
                        <textarea
                          className="form-control"
                          id={element.id}
                          onChange={(e) =>
                            setPolicyItemRequirement({
                              ...policyItemRequirement,
                              [element.id]: e.target.value,
                            })
                          }
                          value={
                            policyItemRequirement[element.id]
                              ? policyItemRequirement[element.id]
                              : ""
                          }
                          rows={5}
                        />
                      ) : ["multi", "multiline"].includes(
                          element?.options?.toLowerCase()
                        ) ? (
                        <textarea
                          className="form-control"
                          id={element.id}
                          onChange={(e) =>
                            setPolicyItemRequirement({
                              ...policyItemRequirement,
                              [element.id]: e.target.value,
                            })
                          }
                          value={
                            policyItemRequirement[element.id]
                              ? policyItemRequirement[element.id]
                              : ""
                          }
                          rows={5}
                        />
                      ) : (
                        <input
                          className="form-control"
                          id={element.id}
                          onChange={(e) =>
                            setPolicyItemRequirement({
                              ...policyItemRequirement,
                              [element.id]: e.target.value,
                            })
                          }
                          value={
                            policyItemRequirement[element.id]
                              ? policyItemRequirement[element.id]
                              : ""
                          }
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        {requirements?.length > 0
          ? !hideSave && (
              <button className="btn btn-primary" type="submit">
                Save requirement
              </button>
            )
          : "No requirements"}
      </fieldset>
    </form>
  );
};

export default ClientPolicyRequirement;
